/**
 * Generates a UUID v4 using crypto.randomUUID() in a safe way that works in both browser and server environments
 * @returns string A valid UUID v4
 */
export function generateUUID(): string {
  // In browser environments, use window.crypto
  if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }

  // In Node.js or other server environments, use global crypto
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback implementation using crypto.getRandomValues()
  // This is based on the RFC4122 v4 UUID specification
  const getRandomValues = (array: Uint8Array): Uint8Array => {
    if (typeof window !== 'undefined' && window.crypto) {
      return window.crypto.getRandomValues(array);
    }
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      return crypto.getRandomValues(array);
    }
    // Last resort fallback (not cryptographically secure)
    for (let i = 0; i < array.length; i++) {
      array[i] = Math.floor(Math.random() * 256);
    }
    return array;
  };

  const randomBytes = new Uint8Array(16);
  getRandomValues(randomBytes);

  // Set version (4) and variant (2) bits according to RFC4122
  randomBytes[6] = (randomBytes[6] & 0x0f) | 0x40; // version 4
  randomBytes[8] = (randomBytes[8] & 0x3f) | 0x80; // variant 2

  // Convert to hex string with proper UUID format
  const hexBytes = Array.from(randomBytes).map(b => b.toString(16).padStart(2, '0'));
  return [
    hexBytes.slice(0, 4).join(''),
    hexBytes.slice(4, 6).join(''),
    hexBytes.slice(6, 8).join(''),
    hexBytes.slice(8, 10).join(''),
    hexBytes.slice(10, 16).join('')
  ].join('-');
}
